import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { InitiativeStatus } from 'legacy/lut/classes/models/partner360/IFormThingsToImprove'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'
import { useCallback, useMemo, useState } from 'react'

import { useSafetyTranslation } from 'cng-common/features/translations'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'

import { useKeyInitiativesFiltersForm } from '../../hooks/useKeyInitiativesFiltersForm'
import {
  EMPTY_LIST_FILTERS,
  filterInitiativeList,
  getListFilterOptions,
} from '../helpers/listFilters'
import { TListFilterDimension, TListFilters } from '../models'

export const useInitiativeListFilters = ({
  data,
  keyObjectives,
  priorities,
}: {
  data: TKeyInitiative[]
  keyObjectives: IKeyObjective[]
  priorities: IKIBusinessPriority[]
}) => {
  const shared = useKeyInitiativesFiltersForm()
  const t = useSafetyTranslation()
  const [local, setLocal] = useState<TListFilters>(EMPTY_LIST_FILTERS)
  const filters = useMemo<TListFilters>(
    () => ({
      ...local,
      strategicInitiatives: shared.keyObjectives ?? [],
      businessPriorities: shared.businessPriorities ?? [],
      statuses: shared.statuses ?? [],
      countryCodes: shared.countryCodes ?? [],
    }),
    [local, shared.keyObjectives, shared.businessPriorities, shared.statuses, shared.countryCodes]
  )
  const options = useMemo(
    () => getListFilterOptions(data, keyObjectives, priorities, t),
    [data, keyObjectives, priorities, t]
  )
  const filteredKeyInitiatives = useMemo(
    () => filterInitiativeList(data, filters, priorities, shared.keyInitiativeIds),
    [data, filters, priorities, shared.keyInitiativeIds]
  )
  const {
    changeKeyObjectives,
    changeBusinessPriorities,
    changeStatuses,
    changeCountryCodes,
    resetFormData,
  } = shared
  const changeFilter = useCallback(
    (dimension: TListFilterDimension, values: string[]) => {
      switch (dimension) {
        case 'strategicInitiatives':
          changeKeyObjectives(values)
          break
        case 'businessPriorities':
          changeBusinessPriorities(values)
          break
        case 'statuses':
          changeStatuses(values as InitiativeStatus[])
          break
        case 'countryCodes':
          changeCountryCodes(values)
          break
        default:
          setLocal((state) => ({ ...state, [dimension]: values }))
      }
    },
    [changeKeyObjectives, changeBusinessPriorities, changeStatuses, changeCountryCodes]
  )
  const clearFilters = useCallback(() => {
    resetFormData()
    setLocal(EMPTY_LIST_FILTERS)
  }, [resetFormData])
  return {
    filters,
    options,
    filteredKeyInitiatives,
    changeFilter,
    clearFilters,
    quickFilter: shared.quickFilter,
    keyInitiativeIds: shared.keyInitiativeIds,
    resetQuickFilter: shared.resetQuickFilter,
  }
}
